// Where the flow opens, decided once per arrival. The station on screen
// is a function of a PLACEMENT — what the server has said about where
// this family stands — never of how far the credential has got, which
// URL default happens to apply, or what has loaded so far. Before a
// placement exists the flow shows a loading state; once one exists the
// station changes only when the family acts, when the server answers an
// act, or, for a placement the signed handoff's bootstrap made ahead of
// the view, when the view itself confirms or moves it.

import type { QuoteFlowWidth } from "components/Header/Header";

import type { QuoteFlowStepName } from "./analytics";
import { landingStation, serverStation, stationIsAhead } from "./step-routes";

/**
 * `pending`: nothing authoritative yet. `provisional`: the bootstrap's
 * landing, trusted until the view settles it. `settled`: a view has
 * loaded; from here the server's derived step is the placement.
 */
export type Placement =
  | { kind: "pending" }
  | { kind: "provisional"; station: QuoteFlowStepName }
  | { kind: "settled" };

/** The two arrivals that can place: the bootstrap's answer (its
 * `landing`, absent from a server that predates it) and a view. A
 * credential changing hands is deliberately not one of them. */
export type PlacementEvent =
  { type: "bootstrap"; landing: string | undefined } | { type: "view" };

export const PLACEMENT_PENDING: Placement = { kind: "pending" };

const BOOTSTRAP_LANDINGS: Record<string, QuoteFlowStepName> = {
  quiz: "quiz",
  schedule_call: "schedule-call",
  quote: "quote",
};

export function placementReducer(
  current: Placement,
  event: PlacementEvent,
): Placement {
  if (event.type === "view") {
    return current.kind === "settled" ? current : { kind: "settled" };
  }
  if (current.kind !== "pending") return current;
  const station =
    event.landing === undefined ? undefined : BOOTSTRAP_LANDINGS[event.landing];
  return station === undefined ? current : { kind: "provisional", station };
}

/** The station the placement stands on: the server's derivation once
 * settled, the bootstrap's landing while provisional, none while
 * pending. Read from the current view every render, so a step the flow
 * patched after an act is honored at once. */
export function placedStation(
  placement: Placement,
  viewStep: string | null,
): QuoteFlowStepName | null {
  if (placement.kind === "settled") {
    return viewStep === null ? null : serverStation(viewStep);
  }
  if (placement.kind === "provisional") return placement.station;
  return null;
}

/**
 * The station on screen for a placement and the URL's ask. A bare URL
 * that has not yet been written back to means "where I stand", exactly
 * once; afterwards the bare path is the quote station's own address. A
 * `quiz` ask behind the placement renders only when this device holds
 * quiz progress to edit.
 */
export function stationOnScreen(args: {
  placed: QuoteFlowStepName | null;
  requested: QuoteFlowStepName | null;
  bareArrival: boolean;
  quizProgressStored: boolean;
}): QuoteFlowStepName | null {
  const { placed, requested, bareArrival, quizProgressStored } = args;
  if (placed === null) return null;
  if (bareArrival) return placed;
  const ask =
    requested === "quiz" &&
    stationIsAhead(placed, "quiz") &&
    !quizProgressStored
      ? null
      : requested;
  return landingStation(ask ?? placed, placed);
}

/** Every screen the flow can show — the stations and the states around
 * them. `arrival-loading` is the wait before a placement, hinting at no
 * station; `skeleton` is the quote screen's own placeholder, shown only
 * once the quote screen is where the family stands. */
export type ArrivalScreen =
  | "not-found"
  | "error"
  | "done"
  | "finalize"
  | "sign"
  | "payment"
  | "application"
  | "exhausted"
  | "interstitial"
  | "quiz"
  | "schedule-call"
  | "account"
  | "app-loading"
  | "arrival-loading"
  | "skeleton"
  | "unpriced"
  | "out-of-area"
  | "quote";

export interface ScreenFacts {
  fatal: "not_found" | "error" | null;
  placed: QuoteFlowStepName | null;
  station: QuoteFlowStepName | null;
  requested: QuoteFlowStepName | null;
  viewReady: boolean;
  credential: "pending" | "granted" | "refused" | "exhausted";
  /** A refused signature whose presence still lets the cards stand. */
  tier0KeepsCards: boolean;
  pricingReady: boolean;
  /** The flags committed for this quote — the quiz and the quote screen
   * both take their treatment from them, so neither renders before. */
  flagsReady: boolean;
  /** The booked-call read has answered (a call, none, or a failure), or
   * its hold has run out. */
  callReadSettled: boolean;
  pricingOk: boolean;
  /** Pricing has answered that the address lies outside the areas we
   * issue in — nothing further in the flow applies. */
  pricingOutOfArea: boolean;
  formClientReady: boolean;
}

const STATIONS_PAST_QUOTE = new Set<QuoteFlowStepName>([
  "application",
  "review",
  "payment",
  "sign",
  "finalize",
  "done",
]);

/**
 * The one decision the render projects. Dead ends first; then the
 * stations that need the view; then the credential's own dead ends,
 * which stand until a view exists; then the stations the bootstrap can
 * carry; then the loading states — the application's own look for a URL
 * that asks for a station past the quote screen, the neutral mark for
 * anything not yet placed, and the quote screen's placeholder only once
 * that is the station.
 */
export function screenFor(facts: ScreenFacts): ArrivalScreen {
  if (facts.fatal === "not_found") return "not-found";
  if (facts.fatal === "error") return "error";
  if (facts.viewReady) {
    if (facts.station === "done") return "done";
    if (facts.station === "finalize") return "finalize";
    if (facts.station === "sign") return "sign";
    if (facts.station === "payment") return "payment";
    if (
      (facts.station === "application" || facts.station === "review") &&
      facts.formClientReady
    ) {
      return "application";
    }
  }
  // Outside the areas we issue in there is nothing to quiz, book or wait
  // for: the screen that says so stands wherever the flow would have gone.
  if (facts.pricingOutOfArea) return "out-of-area";
  if (!facts.viewReady && facts.credential === "exhausted") return "exhausted";
  if (
    !facts.viewReady &&
    facts.credential === "refused" &&
    !facts.tier0KeepsCards
  ) {
    return "interstitial";
  }
  // The quiz waits for the flags like the quote screen does: its
  // response screens arm their reveal from the variant once, on mount.
  if (facts.station === "quiz") {
    return facts.pricingReady && facts.flagsReady ? "quiz" : "arrival-loading";
  }
  // The schedule-call station waits for the booked-call read, so a reload
  // of a booked station doesn't open on the picker. It doesn't wait for
  // the flags: only its booked face needs them, and that face waits for
  // them itself.
  if (facts.station === "schedule-call") {
    return facts.viewReady && facts.callReadSettled
      ? "schedule-call"
      : "arrival-loading";
  }
  if (facts.station === "account") {
    return facts.viewReady ? "account" : "arrival-loading";
  }
  if (
    !facts.viewReady &&
    facts.requested !== null &&
    STATIONS_PAST_QUOTE.has(facts.requested)
  ) {
    return "app-loading";
  }
  if (facts.placed === null) return "arrival-loading";
  if (!facts.pricingReady || !facts.flagsReady) return "skeleton";
  if (!facts.pricingOk) return "unpriced";
  return "quote";
}

/** The stations whose content sits in the form's column; the quote
 * screen's card grid, and the screens around the flow, are wide. */
const FORM_WIDTH_STATIONS = new Set<QuoteFlowStepName>([
  "quiz",
  "schedule-call",
  "payment",
  "sign",
  "finalize",
  "account",
]);

/**
 * The flow bar's width for a screen. It follows the content under it —
 * the application reports its own column, since its juncture screens
 * widen to the card grid — and, while nothing is on screen yet, the
 * content that is coming: the placed station when one exists, the
 * station a deep link asks for, and the form's column when neither is
 * known, so the bar stands still through the reveal the flow's main
 * arrivals lead to. Only a landing on the quote screen itself widens it.
 */
export function flowBarWidth(args: {
  screen: ArrivalScreen;
  station: QuoteFlowStepName | null;
  requested: QuoteFlowStepName | null;
  applicationWidth: QuoteFlowWidth;
}): QuoteFlowWidth {
  const { screen, station, requested, applicationWidth } = args;
  if (screen === "application") return applicationWidth;
  if (screen === "app-loading") return "form";
  if (screen === "out-of-area") return "form";
  if (screen === "arrival-loading") {
    if (station !== null) return station === "quote" ? "wide" : "form";
    if (requested !== null && requested !== "quote") return "form";
    return "form";
  }
  if (station !== null && FORM_WIDTH_STATIONS.has(station)) return "form";
  return "wide";
}
